import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Search,
  Inventory2,
  PhotoCamera,
  LocalOffer,
} from "@mui/icons-material";

import MainContainer from "../../components/MainContainer";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getBackendUrl } from "../../config";

// O banco grava "Produto"/"Serviço" e "Ativo"/"Inativo" (portugues capitalizado).
const key = (v) => (v || "").trim().toLowerCase();
const isService = (t) => ["serviço", "servico", "service"].includes(key(t));
const isActiveStatus = (s) => ["ativo", "active"].includes(key(s));

const brl = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Espelha services/ProductService/EffectivePrice.ts do backend
const getPrice = (p) => {
  if (!p.isOffer) return Number(p.price);
  const offer = Number(p.offerPrice);
  if (!offer || offer <= 0) return Number(p.price);
  const now = new Date();
  if (p.offerStart && now < new Date(p.offerStart)) return Number(p.price);
  if (p.offerEnd && now > new Date(p.offerEnd)) return Number(p.price);
  return offer;
};

const isOfferActive = (p) => p.isOffer && getPrice(p) !== Number(p.price);

const EMPTY = {
  name: "",
  type: "Produto",
  description: "",
  price: "",
  stock: "",
  isOffer: false,
  offerPrice: "",
  offerStart: "",
  offerEnd: "",
  status: "Ativo",
  categoryId: "",
  durationMinutes: "",
  capacity: 1,
};

// datetime-local exige "YYYY-MM-DDTHH:mm"
const toLocalInput = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const ProdutosServicos = () => {
  const { user } = useContext(AuthContext);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParam, setSearchParam] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [deleting, setDeleting] = useState(null);
  const [categories, setCategories] = useState([]);

  const imageUrlOf = (p) =>
    p.imageUrl
      ? `${getBackendUrl()}/public/company${user.companyId}/products/${p.imageUrl}`
      : null;

  const loadProducts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", {
        params: searchParam ? { searchParam } : {},
      });
      setProducts(data || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    api
      .get("/product-categories")
      .then(({ data }) => setCategories(data || []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(loadProducts, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam]);

  const openNew = () => {
    setForm(EMPTY);
    setEditingId(null);
    setImageFile(null);
    setImagePreview(null);
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setForm({
      name: p.name || "",
      type: isService(p.type) ? "Serviço" : "Produto",
      description: p.description || "",
      price: p.price ?? "",
      stock: p.stock ?? "",
      isOffer: !!p.isOffer,
      offerPrice: p.offerPrice ?? "",
      offerStart: toLocalInput(p.offerStart),
      offerEnd: toLocalInput(p.offerEnd),
      status: isActiveStatus(p.status) ? "Ativo" : "Inativo",
      categoryId: p.categoryId ?? "",
      durationMinutes: p.durationMinutes ?? "",
      capacity: p.capacity ?? 1,
    });
    setEditingId(p.id);
    setImageFile(null);
    setImagePreview(imageUrlOf(p));
    setModalOpen(true);
  };

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handlePickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome.");
      return;
    }
    if (form.price === "" || Number(form.price) < 0) {
      toast.error("Informe um preço válido.");
      return;
    }
    if (form.isOffer && (form.offerPrice === "" || Number(form.offerPrice) <= 0)) {
      toast.error("Informe o preço da oferta.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        description: form.description,
        price: Number(form.price),
        stock: isService(form.type) ? null : Number(form.stock || 0),
        isOffer: form.isOffer,
        offerPrice: form.isOffer ? Number(form.offerPrice) : null,
        offerStart: form.isOffer && form.offerStart ? new Date(form.offerStart) : null,
        offerEnd: form.isOffer && form.offerEnd ? new Date(form.offerEnd) : null,
        status: form.status,
        categoryId: form.categoryId || null,
        // Sem duracao o servico nao aparece para agendamento (e a regra
        // do backend). Produto nunca e agendavel.
        durationMinutes:
          isService(form.type) && form.durationMinutes
            ? Number(form.durationMinutes)
            : null,
        capacity: isService(form.type) ? Number(form.capacity) || 1 : 1,
      };

      let saved;
      if (editingId) {
        const { data } = await api.put(`/products/${editingId}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/products", payload);
        saved = data;
      }

      if (imageFile && saved?.id) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await api.post(`/products/${saved.id}/image`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      toast.success(editingId ? "Produto atualizado!" : "Produto cadastrado!");
      setModalOpen(false);
      loadProducts();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/products/${deleting.id}`);
      toast.success("Excluído com sucesso!");
      loadProducts();
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleting ? `Excluir "${deleting.name}"?` : ""}
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        Esta ação não pode ser desfeita. O histórico de vendas é preservado.
      </ConfirmationModal>

      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
            <Inventory2 /> Produtos e Serviços
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              size="small"
              placeholder="Buscar..."
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" startIcon={<Add />} onClick={openNew}>
              Novo
            </Button>
          </Box>
        </Box>

        <Paper sx={{ p: 0, overflow: "hidden" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Imagem</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Preço</TableCell>
                  <TableCell align="center">Estoque</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      Nenhum produto ou serviço cadastrado.
                    </TableCell>
                  </TableRow>
                )}

                {products.map((p) => {
                  const img = imageUrlOf(p);
                  const onOffer = isOfferActive(p);
                  return (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        {img ? (
                          <Box
                            component="img"
                            src={img}
                            alt={p.name}
                            sx={{ width: 42, height: 42, objectFit: "cover", borderRadius: 1 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                            }}
                          >
                            <PhotoCamera fontSize="small" sx={{ opacity: 0.4 }} />
                          </Box>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{p.name}</Typography>
                        {p.description && (
                          <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>
                            {p.description.length > 60 ? `${p.description.slice(0, 60)}...` : p.description}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={isService(p.type) ? "Serviço" : "Produto"}
                          color={isService(p.type) ? "info" : "default"}
                        />
                      </TableCell>

                      <TableCell align="right">
                        {onOffer ? (
                          <Box>
                            <Typography sx={{ fontSize: 12, textDecoration: "line-through", color: "text.secondary" }}>
                              {brl(p.price)}
                            </Typography>
                            <Typography sx={{ fontWeight: 700, color: "success.main", fontSize: 14, display: "flex", alignItems: "center", gap: 0.5, justifyContent: "flex-end" }}>
                              <LocalOffer sx={{ fontSize: 13 }} />
                              {brl(getPrice(p))}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{brl(p.price)}</Typography>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {isService(p.type) ? (
                          <Typography sx={{ color: "text.secondary" }}>—</Typography>
                        ) : (
                          <Chip
                            size="small"
                            label={p.stock ?? 0}
                            color={Number(p.stock ?? 0) > 0 ? "success" : "error"}
                            variant="outlined"
                          />
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={isActiveStatus(p.status) ? "Ativo" : "Inativo"}
                          color={isActiveStatus(p.status) ? "success" : "default"}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(p)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={() => setDeleting(p)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingId ? "Editar" : "Novo"} Produto/Serviço
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Nome"
                fullWidth
                size="small"
                value={form.name}
                onChange={handleChange("name")}
                autoFocus
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Tipo"
                fullWidth
                size="small"
                value={form.type}
                onChange={handleChange("type")}
              >
                <MenuItem value="Produto">Produto</MenuItem>
                <MenuItem value="Serviço">Serviço</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Categoria"
                fullWidth
                size="small"
                value={form.categoryId}
                onChange={handleChange("categoryId")}
                helperText="Usada para agrupar na loja online"
              >
                <MenuItem value="">Sem categoria</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {isService(form.type) && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Duração (minutos)"
                    type="number"
                    fullWidth
                    size="small"
                    value={form.durationMinutes}
                    onChange={handleChange("durationMinutes")}
                    inputProps={{ min: 1 }}
                    helperText="Necessário para agendamento online"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Vagas por horário"
                    type="number"
                    fullWidth
                    size="small"
                    value={form.capacity}
                    onChange={handleChange("capacity")}
                    inputProps={{ min: 1 }}
                    helperText="1 = atende um cliente por vez"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                label="Descrição"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={form.description}
                onChange={handleChange("description")}
              />
            </Grid>

            <Grid item xs={12} sm={isService(form.type) ? 8 : 4}>
              <TextField
                label="Preço"
                type="number"
                fullWidth
                size="small"
                value={form.price}
                onChange={handleChange("price")}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
            </Grid>

            {!isService(form.type) && (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Estoque"
                  type="number"
                  fullWidth
                  size="small"
                  value={form.stock}
                  onChange={handleChange("stock")}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Status"
                fullWidth
                size="small"
                value={form.status}
                onChange={handleChange("status")}
              >
                <MenuItem value="Ativo">Ativo</MenuItem>
                <MenuItem value="Inativo">Inativo</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button component="label" variant="outlined" size="small" startIcon={<PhotoCamera />}>
                  Imagem
                  <input hidden accept="image/*" type="file" onChange={handlePickImage} />
                </Button>
                {imagePreview && (
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="preview"
                    sx={{ width: 52, height: 52, objectFit: "cover", borderRadius: 1 }}
                  />
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.isOffer} onChange={handleChange("isOffer")} />}
                label="Em oferta"
              />
            </Grid>

            {form.isOffer && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Preço da oferta"
                    type="number"
                    fullWidth
                    size="small"
                    value={form.offerPrice}
                    onChange={handleChange("offerPrice")}
                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Início"
                    type="datetime-local"
                    fullWidth
                    size="small"
                    value={form.offerStart}
                    onChange={handleChange("offerStart")}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Fim"
                    type="datetime-local"
                    fullWidth
                    size="small"
                    value={form.offerEnd}
                    onChange={handleChange("offerEnd")}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    Deixe as datas em branco para a oferta valer sem prazo.
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default ProdutosServicos;
